<template>
  <div class="p-4 max-w-xl mx-auto" v-if="!isLoading">
    <div @click="$router.back()" class="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 cursor-pointer shadow-md transition">
      <BackSvg />
    </div>

    <div class="pb-6 flex flex-col justify-center items-center">
      <h2 class="text-lg text-zinc-700 text-center font-semibold">Terms and Conditions</h2>
    </div>
    <!-- Acceptance of Terms -->
    <section class="space-y-2">
      <h3 class="font-semibold text-gray-900">Acceptance of Terms</h3>
      <p>
        By accessing or using the Interfaced Medical Facilities, Inc. platform (“Platform”), including its website, mobile app, messaging tools, and teleconsultation services
        (“Services”), you agree to be bound by these Terms and Conditions. If you do not agree, you must not use the Platform.
      </p>
    </section>
    <br />

    <!-- Nature of Services -->
    <section class="space-y-2">
      <h3 class="font-semibold text-gray-900">Nature of Services</h3>
      <ul class="list-disc list-inside space-y-1">
        <li>Online medical consultations</li>
        <li>Health assessments</li>
        <li>Electronic medical advice</li>
        <li>Electronic prescriptions (when applicable)</li>
        <li>Follow-up consultations</li>
        <li>Telemonitoring</li>
      </ul>
      <p class="font-medium text-gray-800">
        Note: The Platform itself does not practice medicine. All medical advice is provided solely by independently practicing, PRC-licensed healthcare professionals.
      </p>
    </section>

    <br />
    <!-- Eligibility -->
    <section class="space-y-2">
      <h3 class="font-semibold text-gray-900">Eligibility</h3>
      <ol class="list-decimal list-inside space-y-1">
        <li>At least 18 years old, or represented by a parent/guardian if a minor</li>
        <li>Capable of giving legal consent</li>
        <li>Providing true and accurate personal and medical information</li>
      </ol>
      <p>False or misleading information may result in termination of access to the Services.</p>
    </section>

    <br />
    <!-- No Emergency Services -->
    <section class="space-y-2">
      <h3 class="font-semibold text-gray-900">No Emergency Services</h3>
      <p>The Platform must not be used for emergencies involving:</p>
      <ul class="list-disc list-inside space-y-1">
        <li>Severe difficulty breathing</li>
        <li>Chest pain or suspected heart attack</li>
        <li>Stroke symptoms</li>
        <li>Unresponsiveness</li>
        <li>Severe bleeding</li>
        <li>Any life-threatening condition</li>
      </ul>
      <p class="font-bold">For medical emergencies, go to the nearest hospital immediately.</p>
    </section>

    <br />
    <!-- User Responsibilities -->
    <section class="space-y-2">
      <h3 class="font-semibold text-gray-900">User Responsibilities</h3>
      <ul class="list-disc list-inside space-y-1">
        <li>Provide accurate personal, health, and contact information</li>
        <li>Use the Platform only for lawful and legitimate medical consultations</li>
        <li>Avoid abusive behavior, harassment, or inappropriate conduct toward doctors or staff</li>
        <li>Maintain the confidentiality of your account credentials</li>
        <li>Accept that medical outcomes depend on the accuracy of the information you provide</li>
      </ul>
    </section>

    <br />
    <!-- Doctor-Patient Relationship -->
    <section class="space-y-2">
      <h3 class="font-semibold text-gray-900">Doctor-Patient Relationship</h3>
      <ol class="list-decimal list-inside space-y-1">
        <li>You request a consultation</li>
        <li>A licensed physician accepts and begins evaluating your case</li>
      </ol>
      <p>The relationship ends once the consultation is completed unless continued care is agreed upon.</p>
    </section>

    <br />
    <!-- Limitations of Telemedicine -->
    <section class="space-y-2">
      <h3 class="font-semibold text-gray-900">Limitations of Telecons</h3>
      <ul class="list-disc list-inside space-y-1">
        <li>May not replace physical examination</li>
        <li>Has limitations due to video/audio quality or missing clinical data</li>
        <li>May require in-person consultation for proper diagnosis</li>
        <li>Cannot guarantee specific outcomes</li>
      </ul>
      <p>Doctors may refuse, discontinue, or redirect consultations when clinically appropriate.</p>
    </section>

    <br />
    <!-- Prescriptions and Medications -->
    <section class="space-y-2">
      <h3 class="font-semibold text-gray-900">Prescriptions and Medications</h3>
      <p>Doctors may issue electronic prescriptions only when allowed under:</p>
      <ul class="list-disc list-inside space-y-1">
        <li>The Medical Act</li>
        <li>FDA and DOH guidelines</li>
        <li>PRC regulations</li>
      </ul>
      <p>Certain drugs (e.g., dangerous drugs, controlled substances) cannot be prescribed through teleconsultation.</p>
    </section>

    <br />
    <!-- Fees and Payment -->
    <section class="space-y-2">
      <h3 class="font-semibold text-gray-900">Fees and Payment</h3>
      <ul class="list-disc list-inside space-y-1">
        <li>Consultation fees, subscriptions, or service charges will be shown at the time of booking</li>
        <li>Fees will be charged to your out-patient benefit (if using HMO card)</li>
        <li>Fees are non-refundable except where required by law</li>
        <li>Platform charges may change without prior notice</li>
        <li>Third-party payment processors may be used</li>
      </ul>
    </section>

    <br />
    <!-- Data Privacy & Security -->
    <section class="space-y-2">
      <h3 class="font-semibold text-gray-900">Data Privacy & Security</h3>
      <p>We comply with the Data Privacy Act of 2012 and NPC regulations. Data is collected for:</p>
      <ul class="list-disc list-inside space-y-1">
        <li>Providing teleconsult services</li>
        <li>Maintaining medical records</li>
        <li>Improving platform performance</li>
        <li>Complying with legal obligations</li>
      </ul>
      <p>
        Data security measures include encrypted communication, secure EMR storage, access control, regular audits, and confidentiality agreements. Personal information will not be
        sold or used for advertising without consent. See Privacy Policy for full details.
      </p>
    </section>

    <br />
    <!-- Contact Information -->
    <section class="space-y-2">
      <h3 class="font-semibold text-gray-900">Contact Information</h3>
      <p>
        Email:
        <a href="mailto:contact@interactivemedicalfacilities.com" class="text-blue-600 underline">contact@interactivemedicalfacilities.com</a>
      </p>
    </section>
  </div>

  <div v-else class="flex items-center justify-center h-48">
    <svg class="w-10 h-10 animate-spin text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
    </svg>
  </div>
</template>

<script>
import { ref } from "vue";
import BackSvg from "@/components/svgs/BackSvg.vue";

export default {
  name: "UpdateProfile",
  components: { BackSvg },
  setup() {
    const isLoading = ref(false);
    const isAlert = ref(false);
    const loading = ref(false);

    return {
      isLoading,
      isAlert,
      loading,
    };
  },
};
</script>
